import { IRWCAnalyticsLibrary, UserTraits } from '@ringcentral/rwc-utils/src/analytics/types';
import { OverridedMixpanel } from 'mixpanel-browser';
import mixpanel from 'mixpanel-browser/src/loaders/loader-module-with-async-recorder';
import { getMixpanelMaskedProps } from './analytics-masked-props';

type PayloadType = {
    event: string;
    properties: UserTraits;
};

type QueueEntryType = {
    flushAfter: number;
    id: string;
    payload: PayloadType;
};

export interface IMixpanelExtraAPI extends OverridedMixpanel {
    config?: Record<string, unknown>;
    request_batchers?: {
        events: {
            flush(): void;
            start(): void;
            stop(): void;
            stopped: boolean;
            queue: {
                updatePayloads: (upd: Record<string, PayloadType>) => Array<QueueEntryType>;
                memQueue: Array<QueueEntryType>;
            };
        };
    };
}

export class AnalyticsLibrary implements IRWCAnalyticsLibrary {
    public readonly mixpanel: IMixpanelExtraAPI;

    constructor(mixpanelWriteKey: string, options?: Record<string, unknown>) {
        mixpanel.init(mixpanelWriteKey, options);
        this.mixpanel = mixpanel;
    }

    page(pageName: string, props?: Record<string | number | symbol, unknown>): void {
        this.track(pageName, {
            ...getMixpanelMaskedProps(),
            ...props,
            name: pageName,
        });
    }

    alias(newId: string, previousId: string): void {
        this.mixpanel.alias(newId, previousId);
    }

    reset(): void {
        this.mixpanel.reset();
    }

    track(pageName: string, options?: Record<string, unknown>): void {
        this.mixpanel.track(pageName, options);
    }

    identify(userId: string | null, userTraits: UserTraits) {
        if (userId) {
            this.mixpanel.identify(userId);
        }
        this.mixpanel?.people?.set(userTraits);
        this.mixpanel?.register(userTraits);

        if (this.isBatchingStopped()) {
            this.updateQueuedProperties(traits => ({ ...traits, ...userTraits }));
        }
    }

    setUserProfile(userTraits: UserTraits) {
        this.mixpanel?.people?.set(userTraits);
    }

    removeTraits(userTraits: Array<string>) {
        this.mixpanel?.people?.unset(userTraits);
        userTraits.forEach(trait => this.mixpanel?.unregister(trait));
    }

    getProperty(propertyName: string): unknown {
        return this.mixpanel.get_property(propertyName);
    }

    group(company: string, companyId: string, traits?: UserTraits) {
        const group = this.mixpanel.get_group(company, companyId);

        if (group && traits) group.set_once(traits);
    }

    setConfig(config: Record<string, unknown>): void {
        this.mixpanel.set_config(config);
    }

    updateQueuedProperties(updtFn: (tr: UserTraits) => UserTraits): boolean {
        const queue = this.mixpanel?.request_batchers?.events.queue;

        if (!queue) {
            return false;
        }

        queue.memQueue.forEach(entry => {
            entry.payload.properties = updtFn(entry.payload.properties);
        });

        return true;
    }

    // The method is not a part of public SDK API
    flush() {
        try {
            this.mixpanel.request_batchers?.events.flush();
        } catch (error) {
            throw new Error('mixpanel.request_batchers.events.flush is not available');
        }
    }

    // The method is not a part of public SDK API
    startFlushingBatches() {
        try {
            this.mixpanel.request_batchers?.events.start();
        } catch (error) {
            throw new Error('mixpanel.request_batchers.events.start is not available');
        }
    }

    // The method is not a part of public SDK API
    stopFlushingBatches() {
        try {
            this.mixpanel.request_batchers?.events.stop();
        } catch (error) {
            throw new Error('mixpanel.request_batchers.events.stop is not available');
        }
    }

    // The method is not a part of the public SDK API
    isBatchingStopped() {
        try {
            return this.mixpanel.request_batchers!.events.stopped;
        } catch (error) {
            throw new Error('mixpanel.request_batchers.events.stopped is not available');
        }
    }
}
